import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../context/LanguageContext';
import { HUMPRO_APP_DOWNLOAD_URL } from '../../config/externalLinks';
import { heroImage } from '../../assets/siteImages';
import { heroItem, heroStagger, transitionSnappy } from '../../motion/presets';
import './home-hero.scss';

const MotionLink = motion.create(Link);
const MotionAnchor = motion.a;

const HomeHero = () => {
  const reduce = useReducedMotion();
  const { content } = useTranslation();
  const h = content.home.hero;

  return (
    <motion.section
      className="parent-bg home-hero section-full"
      id="home"
      aria-labelledby="home-hero-title"
      initial="hidden"
      animate="visible"
      variants={heroStagger}
    >
      <div className="container home-hero__inner">
        <div className="home-hero__copy">
          <motion.p className="home-hero__eyebrow" variants={heroItem}>
            {h.eyebrowHi}
          </motion.p>
          <motion.p className="home-hero__eyebrow-en" variants={heroItem}>
            {h.eyebrowEn}
          </motion.p>

          <motion.h1 id="home-hero-title" className="home-hero__title" variants={heroItem}>
            {h.title}
            <span className="home-hero__title-line">{h.titleLine2}</span>
          </motion.h1>

          <motion.p className="home-hero__lead" variants={heroItem}>
            {h.lead}
          </motion.p>

          <motion.div className="home-hero__actions" variants={heroItem}>
            <MotionAnchor
              className="home-hero__playstore"
              href={HUMPRO_APP_DOWNLOAD_URL}
              target="_blank"
              rel="noreferrer"
              aria-label={h.playAria}
              whileHover={reduce ? undefined : { scale: 1.02 }}
              whileTap={reduce ? undefined : { scale: 0.98 }}
              transition={transitionSnappy}
            >
              <span className="home-hero__play-icon" aria-hidden="true" />
              <span className="home-hero__play-text">
                <small>{h.playSmall}</small>
                <strong>{h.playStrong}</strong>
              </span>
            </MotionAnchor>
            <MotionLink
              className="home-hero__cta-courses"
              to="/courses"
              whileHover={reduce ? undefined : { scale: 1.02, y: -1 }}
              whileTap={reduce ? undefined : { scale: 0.98 }}
              transition={transitionSnappy}
            >
              {h.exploreCourses} <span aria-hidden="true">→</span>
            </MotionLink>
            <MotionLink
              className="home-hero__cta-advisor"
              to="/contact"
              whileHover={reduce ? undefined : { scale: 1.02, y: -1 }}
              whileTap={reduce ? undefined : { scale: 0.98 }}
              transition={transitionSnappy}
            >
              {h.talkToAdvisor}
            </MotionLink>
          </motion.div>
        </div>

        <motion.div
          className="home-hero__visual"
          initial={{ opacity: 0, x: 48 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ ...transitionSnappy, delay: 0.35 }}
        >
          <div className="home-hero__figure">
            <motion.img
              className="home-hero__photo"
              src={heroImage}
              alt={h.photoAlt}
              width={540}
              height={640}
              loading="eager"
              decoding="async"
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: 0.28 }}
            />
            <div className="home-hero__badge home-hero__badge--star" aria-hidden="true">
              <span className="home-hero__badge-float-inner">✦</span>
            </div>
            <div className="home-hero__badge home-hero__badge--pill">
              <div className="home-hero__pill-inner">
                <span className="home-hero__pill-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                    <path
                      d="M12 12a4 4 0 100-8 4 4 0 000 8zM4 20a8 8 0 0116 0"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M16 10l2 2 4-4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="home-hero__pill-text">
                  <span className="home-hero__pill-text-hi">{h.pillTextHi}</span>
                  <span className="home-hero__pill-text-en">{h.pillTextEn}</span>
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default HomeHero;
